// Network metadata capture by monkey-patching fetch + XHR.
// Captures method/URL/status/timing only — NO bodies or headers, so there is no redaction work
// for those. URLs have query-string VALUES redacted by default (tokens/PII live there);
// cfg.keepQueryParams lists param names whose values are worth keeping (e.g. ['page','tab']).
//
// Teardown is defensive: we only restore a global if it still points at OUR wrapper (another
// SDK may have wrapped on top of us after start), and an orphaned wrapper goes inert via the
// live flag instead of pushing into a dead store.

export function startNetwork(push, cfg = {}) {
  let live = true;
  const keep = new Set((cfg.keepQueryParams || []).map((k) => String(k).toLowerCase()));

  const redact = (rawUrl) => {
    try {
      const u = new URL(String(rawUrl), location.href);
      if (u.search) {
        const params = new URLSearchParams(u.search);
        for (const key of [...params.keys()]) {
          if (!keep.has(key.toLowerCase())) params.set(key, '***');
        }
        u.search = params.toString();
      }
      u.hash = '';
      // Same-origin URLs shrink to path+query for readability; cross-origin keep the origin.
      return u.origin === location.origin ? u.pathname + u.search : u.href;
    } catch (_) {
      return String(rawUrl).split('?')[0];
    }
  };

  const origFetch = window.fetch;
  const wrappedFetch = function (input, init) {
    const start = performance.now();
    const method = (init && init.method) || (input && input.method) || 'GET';
    const url = typeof input === 'string' ? input : (input && input.url) || String(input);
    return origFetch.apply(this, arguments).then(
      (res) => {
        if (live) push({ type: 'network', method, url: redact(url), status: res.status, durationMs: Math.round(performance.now() - start) });
        return res;
      },
      (err) => {
        if (live) push({ type: 'network', method, url: redact(url), status: 0, error: String(err), durationMs: Math.round(performance.now() - start) });
        throw err;
      }
    );
  };
  window.fetch = wrappedFetch;

  const OrigOpen = XMLHttpRequest.prototype.open;
  const OrigSend = XMLHttpRequest.prototype.send;

  const wrappedOpen = function (method, url) {
    this.__ff = { method, url, start: 0 };
    return OrigOpen.apply(this, arguments);
  };

  const wrappedSend = function () {
    // One listener per XHR instance (a reused XHR must not accumulate listeners); it reads
    // the CURRENT __ff at fire time so reuse reports the right request.
    if (this.__ff && !this.__ffBound) {
      this.__ffBound = true;
      this.addEventListener('loadend', () => {
        const meta = this.__ff;
        if (meta && live) {
          push({ type: 'network', method: meta.method, url: redact(meta.url), status: this.status, durationMs: Math.round(performance.now() - meta.start) });
        }
      });
    }
    if (this.__ff) this.__ff.start = performance.now();
    return OrigSend.apply(this, arguments);
  };

  XMLHttpRequest.prototype.open = wrappedOpen;
  XMLHttpRequest.prototype.send = wrappedSend;

  return () => {
    live = false;
    if (window.fetch === wrappedFetch) window.fetch = origFetch;
    if (XMLHttpRequest.prototype.open === wrappedOpen) XMLHttpRequest.prototype.open = OrigOpen;
    if (XMLHttpRequest.prototype.send === wrappedSend) XMLHttpRequest.prototype.send = OrigSend;
  };
}
